/*
 * JosyTCG thema — lades, winkelwagen, zoeksuggesties, filters en de productpagina.
 *
 * Klassen die hier worden aan- en uitgezet (is-open, opacity-60, pointer-events-none,
 * overflow-hidden, border-gold, border-line) leest Tailwind ook uit dit bestand.
 */
(() => {
  "use strict";

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const config = window.theme || {};
  const routes = config.routes || {};
  const pageType = document.body.dataset.pageType;

  /* ----------------------------------------------------------------------- */
  /* Lades: mobiel menu, filters, winkelwagen                                */
  /* ----------------------------------------------------------------------- */

  let lastFocus = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add("is-open");
    document.documentElement.classList.add("overflow-hidden");
    $$(`[aria-controls="${drawer.id}"]`).forEach((el) => el.setAttribute("aria-expanded", "true"));
    const focusTarget = $("[data-autofocus]", drawer) || $("a, button, input", drawer);
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains("is-open")) return;
    drawer.classList.remove("is-open");
    $$(`[aria-controls="${drawer.id}"]`).forEach((el) => el.setAttribute("aria-expanded", "false"));
    if (!$("[data-drawer].is-open")) document.documentElement.classList.remove("overflow-hidden");
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener("click", (event) => {
    const opener = event.target.closest("[data-drawer-open]");
    if (opener) {
      const drawer = document.getElementById(opener.getAttribute("data-drawer-open"));
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer);
      }
      return;
    }

    const closer = event.target.closest("[data-drawer-close]");
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest("[data-drawer]"));
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    closeSearch();
    const open = $$("[data-drawer].is-open").pop();
    if (open) closeDrawer(open);
  });

  // Vanaf desktopbreedte zijn het menu en de filters geen lade meer.
  window.matchMedia("(min-width: 1024px)").addEventListener("change", (query) => {
    if (query.matches) ["MobileMenu", "FilterDrawer"].forEach((id) => closeDrawer(document.getElementById(id)));
  });

  /* ----------------------------------------------------------------------- */
  /* Winkelwagen                                                             */
  /* ----------------------------------------------------------------------- */

  /** Secties die na een wijziging opnieuw gerenderd moeten worden. */
  function renderIds() {
    return [...new Set($$("[data-render-id]").map((el) => el.dataset.renderId))];
  }

  function renderSections(sections) {
    if (!sections) return;
    for (const [id, html] of Object.entries(sections)) {
      if (!html) continue;
      const fresh = new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(`[data-render-id="${id}"]`);
      if (!fresh) continue;
      $$(`[data-render-id="${id}"]`).forEach((el) => {
        el.innerHTML = fresh.innerHTML;
      });
    }
    updateCount();
  }

  function updateCount() {
    const source = $("[data-cart-count-source]");
    if (!source) return;
    const count = Number(source.dataset.cartCountSource) || 0;
    $$("[data-cart-count]").forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  }

  async function cartRequest(path, body) {
    const ids = renderIds();
    let options;

    if (body instanceof FormData) {
      body.append("sections", ids.join(","));
      body.append("sections_url", location.pathname);
      options = { method: "POST", body, headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" } };
    } else {
      options = {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...body, sections: ids, sections_url: location.pathname }),
      };
    }

    const response = await fetch(`${path}.js`, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || "Er ging iets mis. Probeer het opnieuw.");
    }
    return data;
  }

  function showCartError(message, scope) {
    const target = (scope && $("[data-cart-error]", scope)) || $("#CartDrawer [data-cart-error]");
    if (!target) {
      window.alert(message);
      return;
    }
    target.textContent = message;
    target.hidden = false;
    if (target.closest("#CartDrawer")) openDrawer($("#CartDrawer"));
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute("aria-busy", String(busy));
    const label = $("[data-add-label]", button);
    if (!label) return;
    if (busy) {
      label.dataset.idle = label.textContent;
      label.textContent = "Bezig…";
    } else if (label.dataset.idle) {
      label.textContent = label.dataset.idle;
    }
  }

  // In winkelwagen: formulier onderschept, product toegevoegd, lade open.
  document.addEventListener("submit", async (event) => {
    const form = event.target.closest("form[data-ajax-cart]");
    if (!form) return;
    event.preventDefault();

    const button = event.submitter || $("[type=submit]", form);
    const error = $("[data-cart-error]", form);
    if (error) error.hidden = true;
    setBusy(button, true);

    try {
      const data = await cartRequest(routes.cart_add_url || "/cart/add", new FormData(form));
      if (config.cartDrawer === false || pageType === "cart") {
        window.location.href = routes.cart_url || "/cart";
        return;
      }
      renderSections(data.sections);
      openDrawer($("#CartDrawer"));
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      } else {
        showCartError(err.message);
      }
    } finally {
      setBusy(button, false);
    }
  });

  // Aantal aanpassen of verwijderen, in de lade en op de winkelwagenpagina.
  document.addEventListener("click", async (event) => {
    const link = event.target.closest("[data-cart-change]");
    if (!link) return;
    event.preventDefault();

    const scope = link.closest("[data-render-id]");
    if (scope) scope.classList.add("opacity-60", "pointer-events-none");

    try {
      const data = await cartRequest(routes.cart_change_url || "/cart/change", {
        line: Number(link.dataset.line),
        quantity: Number(link.dataset.quantity),
      });
      renderSections(data.sections);
    } catch (err) {
      showCartError(err.message, scope);
    } finally {
      if (scope) scope.classList.remove("opacity-60", "pointer-events-none");
    }
  });

  // Opmerking bij de bestelling meteen bewaren, ook als de klant nog verder winkelt.
  document.addEventListener("change", (event) => {
    if (!event.target.matches("[data-cart-note]")) return;
    fetch(`${routes.cart_update_url || "/cart/update"}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ note: event.target.value }),
    }).catch(() => {});
  });

  /* ----------------------------------------------------------------------- */
  /* Zoeksuggesties in de header                                             */
  /* ----------------------------------------------------------------------- */

  const searchRoot = $("[data-predictive-search]");
  const searchInput = searchRoot && $("input[name=q]", searchRoot);
  const searchResults = searchRoot && $("[data-predictive-results]", searchRoot);
  let searchTimer;
  let searchController;

  function closeSearch() {
    if (!searchResults) return;
    searchResults.hidden = true;
    searchInput.setAttribute("aria-expanded", "false");
  }

  if (searchRoot && searchInput && searchResults) {
    // Kort wachten zodat er niet per toetsaanslag een aanroep gaat.
    searchInput.addEventListener("input", () => {
      clearTimeout(searchTimer);
      const term = searchInput.value.trim();
      if (term.length < 2) {
        if (searchController) searchController.abort();
        searchResults.innerHTML = "";
        closeSearch();
        return;
      }

      searchTimer = setTimeout(async () => {
        if (searchController) searchController.abort();
        searchController = new AbortController();
        const url =
          `${searchRoot.dataset.url}?q=${encodeURIComponent(term)}` +
          "&resources[type]=product&resources[limit]=6" +
          "&resources[options][unavailable_products]=last&section_id=predictive-search";

        try {
          const response = await fetch(url, { signal: searchController.signal });
          const html = await response.text();
          const content = new DOMParser().parseFromString(html, "text/html").querySelector("[data-predictive-content]");
          if (!content || searchInput.value.trim() !== term) return;
          searchResults.innerHTML = content.innerHTML;
          searchResults.hidden = false;
          searchInput.setAttribute("aria-expanded", "true");
        } catch (err) {
          if (err.name !== "AbortError") closeSearch();
        }
      }, 250);
    });

    searchInput.addEventListener("focus", () => {
      if (searchResults.innerHTML.trim() && searchInput.value.trim().length >= 2) {
        searchResults.hidden = false;
        searchInput.setAttribute("aria-expanded", "true");
      }
    });

    document.addEventListener("mousedown", (event) => {
      if (!searchRoot.contains(event.target)) closeSearch();
    });
  }

  /* ----------------------------------------------------------------------- */
  /* Filters in de shop                                                      */
  /* ----------------------------------------------------------------------- */

  const facetsForm = $("[data-facets-form]");
  if (facetsForm) {
    const desktop = window.matchMedia("(min-width: 1024px)");

    const submitFacets = () => {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(facetsForm)) {
        if (value !== "") params.append(key, value);
      }
      const query = params.toString();
      const action = facetsForm.getAttribute("action");
      window.location.assign(query ? `${action}?${query}` : action);
    };

    // Desktop: direct toepassen. Mobiel: pas bij "Filters toepassen", zodat de lade open blijft.
    const onChange = (target) => {
      if (desktop.matches || (target && target.matches("[data-autosubmit]"))) submitFacets();
    };

    // Een al geselecteerde radio vuurt geen change-event, en juist die klik moet het
    // filter weer uitzetten.
    facetsForm.addEventListener("mousedown", (event) => {
      const label = event.target.closest("label");
      const radio = label && $("input[type=radio][data-toggleable]", label);
      if (radio) radio.dataset.wasChecked = String(radio.checked);
    });
    facetsForm.addEventListener("click", (event) => {
      const radio = event.target;
      if (!(radio instanceof HTMLInputElement) || radio.type !== "radio" || !radio.hasAttribute("data-toggleable")) return;
      if (radio.dataset.wasChecked === "true") {
        radio.checked = false;
        radio.dataset.wasChecked = "false";
        onChange(radio);
      }
    });

    facetsForm.addEventListener("change", (event) => onChange(event.target));
    facetsForm.addEventListener("submit", (event) => {
      event.preventDefault();
      submitFacets();
    });
  }

  /* ----------------------------------------------------------------------- */
  /* Productpagina                                                           */
  /* ----------------------------------------------------------------------- */

  function syncQuantity(wrapper) {
    const input = $("[data-qty-input]", wrapper);
    if (!input) return;
    const max = Number(input.max) || 99;
    let value = Math.round(Number(input.value)) || 1;
    value = Math.min(max, Math.max(1, value));
    input.value = value;
    const minus = $("[data-qty-minus]", wrapper);
    const plus = $("[data-qty-plus]", wrapper);
    if (minus) minus.disabled = value <= 1;
    if (plus) plus.disabled = value >= max;
  }

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-qty-minus], [data-qty-plus]");
    if (!button) return;
    const wrapper = button.closest("[data-qty]");
    const input = $("[data-qty-input]", wrapper);
    input.value = (Number(input.value) || 1) + (button.hasAttribute("data-qty-plus") ? 1 : -1);
    syncQuantity(wrapper);
  });

  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-qty-input]")) syncQuantity(event.target.closest("[data-qty]"));
  });

  // Fotogalerij
  document.addEventListener("click", (event) => {
    const thumb = event.target.closest("[data-thumb]");
    if (!thumb) return;
    const root = thumb.closest("[data-product]");
    const image = root && $("[data-main-image]", root);
    if (image) {
      image.removeAttribute("srcset");
      image.src = thumb.dataset.src;
      if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
      if (thumb.dataset.alt) image.alt = thumb.dataset.alt;
    }
    $$("[data-thumb]", root).forEach((item) => {
      const active = item === thumb;
      item.classList.toggle("border-gold", active);
      item.classList.toggle("border-line", !active);
      item.setAttribute("aria-current", String(active));
    });
  });

  function initProduct(root) {
    const dataElement = $("[data-variants]", root);
    if (!dataElement || root.dataset.productReady) return;
    root.dataset.productReady = "true";

    let variants = [];
    try {
      variants = JSON.parse(dataElement.textContent);
    } catch {
      return;
    }

    $$("[data-qty]", root).forEach(syncQuantity);

    root.addEventListener("change", (event) => {
      if (!event.target.matches("[data-option]")) return;
      const selected = $$("[data-option-group]", root).map((group) => {
        const checked = $("input:checked", group);
        return checked ? checked.value : null;
      });
      const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
      updateVariant(root, variant);
    });
  }

  function updateVariant(root, variant) {
    const button = $("[data-add-button]", root);
    const label = $("[data-add-label]", root);

    if (!variant) {
      if (button) button.disabled = true;
      if (label) label.textContent = "Niet beschikbaar";
      return;
    }

    const idInput = $("[data-variant-id]", root);
    if (idInput) idInput.value = variant.id;

    const price = $("[data-price]", root);
    if (price) price.textContent = variant.price;

    const compare = $("[data-compare]", root);
    if (compare) {
      compare.textContent = variant.compare || "";
      compare.hidden = !variant.compare;
    }

    const stock = $("[data-stock]", root);
    if (stock) stock.innerHTML = variant.status;

    if (button) button.disabled = !variant.available;
    if (label) label.textContent = variant.label;

    const restock = $("[data-restock]", root);
    if (restock) restock.hidden = variant.available;

    const sku = $("[data-sku]", root);
    if (sku && variant.sku) sku.textContent = variant.sku;

    const quantity = $("[data-qty-input]", root);
    if (quantity) {
      quantity.max = variant.max;
      syncQuantity(quantity.closest("[data-qty]"));
    }

    if (variant.media) {
      const thumb = $(`[data-thumb][data-media-id="${variant.media}"]`, root);
      if (thumb) thumb.click();
    }

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState(null, "", url);
  }

  /* ----------------------------------------------------------------------- */
  /* Aanbevolen producten                                                    */
  /* ----------------------------------------------------------------------- */

  async function loadRecommendations(container) {
    if (!container.dataset.url || container.dataset.loaded) return;
    container.dataset.loaded = "true";
    try {
      const response = await fetch(container.dataset.url);
      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, "text/html").querySelector("[data-recommendations]");
      if (fresh && fresh.innerHTML.trim()) container.innerHTML = fresh.innerHTML;
    } catch {
      // Geen aanbevelingen is geen ramp; de sectie blijft dan leeg.
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Opstarten, ook na het herladen van een sectie in de thema-editor        */
  /* ----------------------------------------------------------------------- */

  function init(root = document) {
    $$("[data-product]", root).forEach(initProduct);
    $$("[data-recommendations]", root).forEach(loadRecommendations);

    // Datum op het herroepingsformulier
    const today = new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
    $$("[data-today]", root).forEach((el) => {
      if (el.tagName === "INPUT") el.value = today;
      else el.textContent = today;
    });
  }

  init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
